/**
 * Calendar-day keys for the admin app's date-scoped tables.
 *
 * A service date is a date, not an instant. "Closed Christmas Day" and "the
 * shift notes from Tuesday" must mean the same thing to a manager in the
 * restaurant and to a server process running in UTC, so these tables key on a
 * plain "YYYY-MM-DD" string in the restaurant's own calendar rather than on a
 * timestamp.
 *
 * Keys are also range-scanned lexicographically (`gte`/`lte` on the
 * by_location_date indexes), which only works while every stored key is
 * well-formed and zero-padded — hence the validators here. A malformed key
 * writes a row that no range read will ever return again.
 */

/**
 * Platform-wide assumption, NOT a Tuscan value: every restaurant currently on
 * the platform operates in Pacific time. When a location outside PT is
 * provisioned this must come from that location's config — a wrong zone shifts
 * every date key by up to a day.
 */
export const PLATFORM_TIME_ZONE = "America/Los_Angeles";

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Today's calendar date in the restaurant's timezone, as "YYYY-MM-DD". */
export function todayDateKey(now: number = Date.now()): string {
  return new Date(now).toLocaleDateString("en-CA", {
    timeZone: PLATFORM_TIME_ZONE,
  });
}

/** True when `value` is a well-formed key naming a date that actually exists. */
export function isValidDateKey(value: string): boolean {
  if (!DATE_KEY_PATTERN.test(value)) return false;

  // Round-trips through UTC to reject "2026-02-30" and friends, which the
  // pattern alone accepts.
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

export function assertValidDateKey(value: string, field = "dateKey"): void {
  if (!isValidDateKey(value)) {
    throw new Error(`${field} must be a calendar date formatted YYYY-MM-DD`);
  }
}

/** True for a 24-hour "HH:MM" clock time. */
export function isValidTimeOfDay(value: string): boolean {
  return TIME_OF_DAY_PATTERN.test(value);
}

export function assertValidTimeOfDay(value: string, field: string): void {
  if (!isValidTimeOfDay(value)) {
    throw new Error(`${field} must be a 24-hour time formatted HH:MM`);
  }
}

/** Shifts a date key by whole days, staying in the calendar domain. */
export function addDaysToDateKey(dateKey: string, days: number): string {
  assertValidDateKey(dateKey);
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + days))
    .toISOString()
    .slice(0, 10);
}

/**
 * Bounds a client-supplied page size. The id comes from a phone the operator
 * controls, so `limit` is untrusted input like any other: unbounded values turn
 * one tap into a full-table read, and NaN silently truncates the page to
 * nothing.
 */
export function clampLimit(
  value: number | undefined,
  fallback: number,
  max: number,
): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.min(Math.max(Math.trunc(value), 1), max);
}
